import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Calendar,
  Gift,
  Users,
  FileText,
  BookOpen,
  Sparkles,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  Plus,
  Eye,
  Loader2,
  Shield,
  Star,
  Heart,
  Cake,
  Send,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "overview", label: "Overview", icon: Eye },
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "birthdays", label: "Birthdays", icon: Cake },
  { id: "loyalty", label: "Loyalty", icon: Star },
  { id: "weddings", label: "Weddings", icon: Heart },
  { id: "events", label: "Events", icon: Sparkles },
  { id: "gift_cards", label: "Gift Cards", icon: Gift },
  { id: "inquiries", label: "Inquiries", icon: FileText },
  { id: "registrations", label: "Registrations", icon: BookOpen },
  { id: "intake_forms", label: "Intake Forms", icon: Users },
];

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-yellow-500/10 text-yellow-400",
    approved: "bg-emerald-500/10 text-emerald-400",
    confirmed: "bg-emerald-500/10 text-emerald-400",
    active: "bg-emerald-500/10 text-emerald-400",
    rejected: "bg-red-500/10 text-red-400",
    cancelled: "bg-red-500/10 text-red-400",
    new: "bg-blue-500/10 text-blue-400",
    redeemed: "bg-purple-500/10 text-purple-400",
  };
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 ${styles[status] || "bg-[#262626] text-[#A3A3A3]"}`}>
      {status}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, color = "text-[#C5A059]" }) => (
  <div className="bento-card p-5">
    <div className="flex items-center gap-3 mb-2">
      <Icon size={16} className={color} />
      <span className="font-body text-xs tracking-wider uppercase text-[#A3A3A3]">{label}</span>
    </div>
    <span className="font-heading text-2xl text-white">{value}</span>
  </div>
);

const TableHeader = ({ children }) => (
  <th className="font-body text-[10px] tracking-wider uppercase text-[#A3A3A3] text-left py-3 px-4 border-b border-[#262626]">
    {children}
  </th>
);

const TableCell = ({ children, className = "" }) => (
  <td className={`font-body text-sm text-[#A3A3A3] py-3 px-4 border-b border-[#1a1a1a] ${className}`}>
    {children}
  </td>
);

export const AdminDashboardPage = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [authenticated, setAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [data, setData] = useState({
    bookings: [],
    events: [],
    gift_cards: [],
    inquiries: [],
    registrations: [],
    intake_forms: [],
    loyalty_cards: [],
    weddings: [],
  });
  const [loading, setLoading] = useState(true);
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: "", description: "", event_type: "promotion", date: "", end_date: "",
    image_url: "", discount: "", promo_code: "", active: true,
  });
  const [submittingEvent, setSubmittingEvent] = useState(false);

  const ADMIN_PIN = "2024";

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pinInput === ADMIN_PIN) {
      setAuthenticated(true);
      toast.success("Welcome, Admin");
    } else {
      toast.error("Incorrect PIN");
      setPinInput("");
    }
  };

  const [birthdayMonth, setBirthdayMonth] = useState(new Date().toLocaleString('default', { month: 'long' }));
  const [birthdayClients, setBirthdayClients] = useState([]);
  const [loadingBirthdays, setLoadingBirthdays] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    if (authenticated) {
      fetchAllData();
    }
  }, [authenticated]);

  useEffect(() => {
    if (authenticated && activeTab === "birthdays") {
      fetchBirthdayClients(birthdayMonth);
    }
  }, [authenticated, activeTab, birthdayMonth]);

  const fetchBirthdayClients = async (month) => {
    setLoadingBirthdays(true);
    try {
      const res = await axios.get(`${API}/admin/birthday-clients?month=${month}`);
      setBirthdayClients(res.data.clients || []);
    } catch { setBirthdayClients([]); }
    finally { setLoadingBirthdays(false); }
  };

  const handleSendBirthdayEmails = async () => {
    if (!window.confirm(`Send birthday promotion emails to all ${birthdayMonth} birthday clients?`)) return;
    setSendingEmails(true);
    setSendResult(null);
    try {
      const res = await axios.post(`${API}/admin/send-birthday-emails?month=${birthdayMonth}`);
      setSendResult(res.data);
      toast.success(`${res.data.sent} birthday emails sent!`);
    } catch { toast.error("Failed to send birthday emails"); }
    finally { setSendingEmails(false); }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [bookings, events, giftCards, inquiries, registrations, intakeForms, loyaltyCards, weddings] = await Promise.all([
        axios.get(`${API}/bookings`).then(r => r.data).catch(() => []),
        axios.get(`${API}/events?active_only=false`).then(r => r.data).catch(() => []),
        axios.get(`${API}/gift-cards`).then(r => r.data).catch(() => []),
        axios.get(`${API}/inquiries`).then(r => r.data).catch(() => []),
        axios.get(`${API}/course-registrations`).then(r => r.data).catch(() => []),
        axios.get(`${API}/intake-forms`).then(r => r.data).catch(() => []),
        axios.get(`${API}/loyalty/all`).then(r => r.data).catch(() => []),
        axios.get(`${API}/wedding-inquiries`).then(r => r.data).catch(() => []),
      ]);
      setData({ bookings, events, gift_cards: giftCards, inquiries, registrations, intake_forms: intakeForms, loyalty_cards: loyaltyCards, weddings });
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setSubmittingEvent(true);
    try {
      await axios.post(`${API}/events`, eventForm);
      toast.success("Event created!");
      setShowEventForm(false);
      setEventForm({ title: "", description: "", event_type: "promotion", date: "", end_date: "", image_url: "", discount: "", promo_code: "", active: true });
      fetchAllData();
    } catch {
      toast.error("Failed to create event");
    } finally {
      setSubmittingEvent(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success("Event deleted");
      fetchAllData();
    } catch {
      toast.error("Failed to delete event");
    }
  };

  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=${newStatus}`);
      toast.success(`Booking ${newStatus}`);
      fetchAllData();
    } catch {
      toast.error("Failed to update booking");
    }
  };

  if (!authenticated) {
    return (
      <div data-testid="admin-login" className="pt-20 min-h-screen bg-[#050505] flex items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-sm w-full mx-4">
          <div className="bento-card p-8 text-center">
            <Shield className="w-12 h-12 text-[#C5A059] mx-auto mb-4" />
            <h1 className="font-heading text-2xl text-white mb-2">Admin Access</h1>
            <p className="font-body text-sm text-[#A3A3A3] mb-6">Enter your admin PIN to continue</p>
            <form onSubmit={handlePinSubmit}>
              <input
                type="password"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                placeholder="Enter PIN"
                data-testid="admin-pin-input"
                className="w-full luxury-input text-center text-lg tracking-[0.3em] mb-4"
                maxLength={10}
              />
              <button
                type="submit"
                data-testid="admin-pin-submit"
                className="w-full bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase py-3 hover:brightness-110 transition-all"
              >
                Access Dashboard
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  const renderOverview = () => (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <StatCard icon={Calendar} label="Bookings" value={data.bookings.length} />
        <StatCard icon={Star} label="Loyalty" value={data.loyalty_cards.length} />
        <StatCard icon={Heart} label="Weddings" value={data.weddings.length} />
        <StatCard icon={Sparkles} label="Events" value={data.events.length} />
        <StatCard icon={Gift} label="Gift Cards" value={data.gift_cards.length} />
        <StatCard icon={FileText} label="Inquiries" value={data.inquiries.length} />
        <StatCard icon={BookOpen} label="Registrations" value={data.registrations.length} />
        <StatCard icon={Users} label="Intake Forms" value={data.intake_forms.length} />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bento-card p-6">
          <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">Recent Bookings</h3>
          {data.bookings.length === 0 ? (
            <p className="text-sm text-[#A3A3A3]">No bookings yet</p>
          ) : (
            <div className="space-y-3">
              {data.bookings.slice(0, 5).map((b) => (
                <div key={b.id} className="flex justify-between items-center text-sm border-b border-[#1a1a1a] pb-2">
                  <div>
                    <span className="text-white">{b.name}</span>
                    <span className="block text-xs text-[#A3A3A3]">{b.date} at {b.time}</span>
                  </div>
                  <StatusBadge status={b.status} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bento-card p-6">
          <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">Recent Gift Cards</h3>
          {data.gift_cards.length === 0 ? (
            <p className="text-sm text-[#A3A3A3]">No gift cards yet</p>
          ) : (
            <div className="space-y-3">
              {data.gift_cards.slice(0, 5).map((gc) => (
                <div key={gc.id} className="flex justify-between items-center text-sm border-b border-[#1a1a1a] pb-2">
                  <div>
                    <span className="text-white">{gc.code}</span>
                    <span className="block text-xs text-[#A3A3A3]">For: {gc.recipient_name}</span>
                  </div>
                  <span className="font-heading text-[#C5A059]">${gc.amount?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderBookings = () => (
    <div className="bento-card overflow-hidden">
      <div className="p-6 border-b border-[#262626]">
        <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">All Bookings ({data.bookings.length})</h3>
      </div>
      {data.bookings.length === 0 ? (
        <div className="p-8 text-center text-[#A3A3A3]">No bookings yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <TableHeader>Client</TableHeader>
              <TableHeader>Phone</TableHeader>
              <TableHeader>Services</TableHeader>
              <TableHeader>Date</TableHeader>
              <TableHeader>Time</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr></thead>
            <tbody>
              {data.bookings.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                  <TableCell className="text-white">{b.name}</TableCell>
                  <TableCell>{b.phone}</TableCell>
                  <TableCell><span className="text-xs">{b.services?.join(", ")}</span></TableCell>
                  <TableCell>{b.date}</TableCell>
                  <TableCell>{b.time}</TableCell>
                  <TableCell><StatusBadge status={b.status} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {b.status !== "confirmed" && (
                        <button onClick={() => handleUpdateBookingStatus(b.id, "confirmed")}
                          className="text-emerald-400 hover:text-emerald-300 p-1" title="Confirm">
                          <CheckCircle size={14} />
                        </button>
                      )}
                      {b.status !== "cancelled" && (
                        <button onClick={() => handleUpdateBookingStatus(b.id, "cancelled")}
                          className="text-red-400 hover:text-red-300 p-1" title="Cancel">
                          <XCircle size={14} />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderEvents = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">Events ({data.events.length})</h3>
        <button
          onClick={() => setShowEventForm(!showEventForm)}
          data-testid="create-event-btn"
          className="flex items-center gap-2 bg-[#C5A059] text-[#050505] font-semibold text-xs tracking-[0.15em] uppercase px-4 py-2 hover:brightness-110 transition-all"
        >
          <Plus size={14} /> New Event
        </button>
      </div>

      {showEventForm && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
          <form onSubmit={handleCreateEvent} className="bento-card p-6 space-y-4" data-testid="create-event-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-1">Title *</label>
                <input type="text" value={eventForm.title} required
                  onChange={(e) => setEventForm(p => ({...p, title: e.target.value}))}
                  data-testid="event-title-input" placeholder="Event title" className="w-full luxury-input" />
              </div>
              <div>
                <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-1">Type *</label>
                <select value={eventForm.event_type}
                  onChange={(e) => setEventForm(p => ({...p, event_type: e.target.value}))}
                  data-testid="event-type-select"
                  className="w-full bg-transparent border-b border-white/20 text-white py-3 focus:border-[#C5A059] focus:outline-none" style={{ borderRadius: 0 }}>
                  <option value="promotion" className="bg-[#121212]">Promotion</option>
                  <option value="holiday" className="bg-[#121212]">Holiday Sale</option>
                  <option value="seasonal" className="bg-[#121212]">Seasonal</option>
                  <option value="flash_sale" className="bg-[#121212]">Flash Sale</option>
                  <option value="new_course" className="bg-[#121212]">New Course</option>
                  <option value="special" className="bg-[#121212]">Special Event</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-1">Description *</label>
              <textarea value={eventForm.description} required rows={3}
                onChange={(e) => setEventForm(p => ({...p, description: e.target.value}))}
                data-testid="event-description-input" placeholder="Event description..." className="w-full luxury-input resize-none" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-1">Start Date</label>
                <input type="text" value={eventForm.date}
                  onChange={(e) => setEventForm(p => ({...p, date: e.target.value}))}
                  placeholder="e.g. March 1, 2026" className="w-full luxury-input" />
              </div>
              <div>
                <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-1">End Date</label>
                <input type="text" value={eventForm.end_date}
                  onChange={(e) => setEventForm(p => ({...p, end_date: e.target.value}))}
                  placeholder="e.g. March 15, 2026" className="w-full luxury-input" />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-1">Discount</label>
                <input type="text" value={eventForm.discount}
                  onChange={(e) => setEventForm(p => ({...p, discount: e.target.value}))}
                  placeholder="e.g. 20% OFF" className="w-full luxury-input" />
              </div>
              <div>
                <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-1">Promo Code</label>
                <input type="text" value={eventForm.promo_code}
                  onChange={(e) => setEventForm(p => ({...p, promo_code: e.target.value}))}
                  placeholder="e.g. HOLIDAY20" className="w-full luxury-input" />
              </div>
              <div>
                <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-1">Image URL</label>
                <input type="text" value={eventForm.image_url}
                  onChange={(e) => setEventForm(p => ({...p, image_url: e.target.value}))}
                  placeholder="Optional image URL" className="w-full luxury-input" />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={submittingEvent} data-testid="submit-event-btn"
                className="bg-[#C5A059] text-[#050505] font-semibold text-xs tracking-[0.15em] uppercase px-6 py-3 hover:brightness-110 transition-all disabled:opacity-40 flex items-center gap-2">
                {submittingEvent ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Create Event
              </button>
              <button type="button" onClick={() => setShowEventForm(false)}
                className="border border-[#262626] text-[#A3A3A3] text-xs tracking-[0.15em] uppercase px-6 py-3 hover:text-white transition-all">
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {data.events.length === 0 ? (
        <div className="bento-card p-8 text-center text-[#A3A3A3]">No events created yet. Click "New Event" to get started.</div>
      ) : (
        <div className="space-y-4">
          {data.events.map((ev) => (
            <div key={ev.id} className="bento-card p-5 flex justify-between items-start" data-testid={`admin-event-${ev.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-white font-semibold">{ev.title}</span>
                  <StatusBadge status={ev.active ? "active" : "inactive"} />
                  <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] bg-[#1a1a1a] px-2 py-0.5">{ev.event_type}</span>
                </div>
                <p className="text-xs text-[#A3A3A3] mb-1">{ev.description?.slice(0, 120)}{ev.description?.length > 120 ? "..." : ""}</p>
                {ev.date && <span className="text-xs text-[#C5A059]">{ev.date}{ev.end_date ? ` — ${ev.end_date}` : ""}</span>}
                {ev.promo_code && <span className="ml-3 text-xs text-[#A3A3A3]">Code: <strong className="text-white">{ev.promo_code}</strong></span>}
              </div>
              <button onClick={() => handleDeleteEvent(ev.id)} className="text-red-400 hover:text-red-300 p-2 ml-4" title="Delete event">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderGiftCards = () => (
    <div className="bento-card overflow-hidden">
      <div className="p-6 border-b border-[#262626] flex justify-between items-center">
        <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">Gift Cards ({data.gift_cards.length})</h3>
        {data.gift_cards.length > 0 && (
          <span className="font-heading text-lg text-[#C5A059]">
            Total: ${data.gift_cards.reduce((sum, gc) => sum + (gc.amount || 0), 0).toLocaleString()} GYD
          </span>
        )}
      </div>
      {data.gift_cards.length === 0 ? (
        <div className="p-8 text-center text-[#A3A3A3]">No gift cards purchased yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <TableHeader>Code</TableHeader>
              <TableHeader>Gift</TableHeader>
              <TableHeader>Buyer</TableHeader>
              <TableHeader>Recipient</TableHeader>
              <TableHeader>Amount</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Date</TableHeader>
            </tr></thead>
            <tbody>
              {data.gift_cards.map((gc) => (
                <tr key={gc.id} data-testid={`gift-card-row-${gc.id}`}>
                  <TableCell className="text-white font-mono">{gc.code}</TableCell>
                  <TableCell>
                    <span className="text-white text-xs">{gc.gift_item || "—"}</span>
                    {gc.gift_type && <span className="block text-[10px] text-[#C5A059] uppercase">{gc.gift_type}</span>}
                  </TableCell>
                  <TableCell>
                    <span className="text-white">{gc.buyer_name}</span>
                    <span className="block text-xs">{gc.buyer_phone}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-white">{gc.recipient_name}</span>
                    {gc.recipient_phone && <span className="block text-xs">{gc.recipient_phone}</span>}
                  </TableCell>
                  <TableCell className="text-[#C5A059]">${gc.amount?.toLocaleString()}</TableCell>
                  <TableCell><StatusBadge status={gc.status} /></TableCell>
                  <TableCell><span className="text-xs">{gc.created_at?.split("T")[0]}</span></TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderInquiries = () => (
    <div className="bento-card overflow-hidden">
      <div className="p-6 border-b border-[#262626]">
        <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">Inquiries ({data.inquiries.length})</h3>
      </div>
      {data.inquiries.length === 0 ? (
        <div className="p-8 text-center text-[#A3A3A3]">No inquiries yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <TableHeader>Name</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Phone</TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Message</TableHeader>
              <TableHeader>Date</TableHeader>
            </tr></thead>
            <tbody>
              {data.inquiries.map((inq) => (
                <tr key={inq.id} data-testid={`inquiry-row-${inq.id}`}>
                  <TableCell className="text-white">{inq.name}</TableCell>
                  <TableCell>{inq.email}</TableCell>
                  <TableCell>{inq.phone}</TableCell>
                  <TableCell><span className="text-xs text-[#C5A059]">{inq.inquiry_type}</span></TableCell>
                  <TableCell><span className="text-xs">{inq.message?.slice(0, 60)}{inq.message?.length > 60 ? "..." : ""}</span></TableCell>
                  <TableCell><span className="text-xs">{inq.created_at?.split("T")[0]}</span></TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderRegistrations = () => (
    <div className="bento-card overflow-hidden">
      <div className="p-6 border-b border-[#262626]">
        <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">Course Registrations ({data.registrations.length})</h3>
      </div>
      {data.registrations.length === 0 ? (
        <div className="p-8 text-center text-[#A3A3A3]">No course registrations yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <TableHeader>Name</TableHeader>
              <TableHeader>Phone</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Course</TableHeader>
              <TableHeader>Message</TableHeader>
              <TableHeader>Date</TableHeader>
            </tr></thead>
            <tbody>
              {data.registrations.map((reg) => (
                <tr key={reg.id} data-testid={`reg-row-${reg.id}`}>
                  <TableCell className="text-white">{reg.name}</TableCell>
                  <TableCell>{reg.phone}</TableCell>
                  <TableCell>{reg.email}</TableCell>
                  <TableCell><span className="text-xs text-[#C5A059]">{reg.course_name}</span></TableCell>
                  <TableCell><span className="text-xs">{reg.message?.slice(0, 60)}</span></TableCell>
                  <TableCell><span className="text-xs">{reg.created_at?.split("T")[0]}</span></TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderIntakeForms = () => (
    <div className="bento-card overflow-hidden">
      <div className="p-6 border-b border-[#262626]">
        <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">Intake Forms ({data.intake_forms.length})</h3>
      </div>
      {data.intake_forms.length === 0 ? (
        <div className="p-8 text-center text-[#A3A3A3]">No intake forms submitted yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <TableHeader>Name</TableHeader>
              <TableHeader>Phone</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Gender</TableHeader>
              <TableHeader>Skin Goals</TableHeader>
              <TableHeader>Date</TableHeader>
            </tr></thead>
            <tbody>
              {data.intake_forms.map((form) => (
                <tr key={form.id} data-testid={`intake-row-${form.id}`}>
                  <TableCell className="text-white">{form.name}</TableCell>
                  <TableCell>{form.phone}</TableCell>
                  <TableCell>{form.email}</TableCell>
                  <TableCell>{form.gender}</TableCell>
                  <TableCell><span className="text-xs">{form.skin_goals?.slice(0, 60)}</span></TableCell>
                  <TableCell><span className="text-xs">{form.created_at?.split("T")[0]}</span></TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderLoyalty = () => (
    <div className="bento-card overflow-hidden">
      <div className="p-6 border-b border-[#262626]">
        <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">Loyalty Punch Cards ({data.loyalty_cards.length})</h3>
      </div>
      {data.loyalty_cards.length === 0 ? (
        <div className="p-8 text-center text-[#A3A3A3]">No loyalty cards yet. Cards are created automatically when clients book.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <TableHeader>Client</TableHeader>
              <TableHeader>Phone</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Total Visits</TableHeader>
              <TableHeader>Current Cycle</TableHeader>
              <TableHeader>Rewards Earned</TableHeader>
              <TableHeader>Rewards Available</TableHeader>
            </tr></thead>
            <tbody>
              {data.loyalty_cards.map((lc) => {
                const available = (lc.rewards_earned || 0) - (lc.rewards_redeemed || 0);
                const cycle = (lc.total_punches || 0) % 9;
                return (
                  <tr key={lc.id}>
                    <TableCell className="text-white">{lc.name}</TableCell>
                    <TableCell>{lc.phone}</TableCell>
                    <TableCell>{lc.email || "—"}</TableCell>
                    <TableCell className="text-white font-semibold">{lc.total_punches}</TableCell>
                    <TableCell>{cycle}/9</TableCell>
                    <TableCell className="text-[#C5A059]">{lc.rewards_earned}</TableCell>
                    <TableCell>
                      {available > 0 ? (
                        <span className="text-emerald-400 font-semibold">{available} ready</span>
                      ) : (
                        <span className="text-[#A3A3A3]">0</span>
                      )}
                    </TableCell>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderWeddings = () => (
    <div className="bento-card overflow-hidden">
      <div className="p-6 border-b border-[#262626]">
        <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">Wedding Inquiries ({data.weddings.length})</h3>
      </div>
      {data.weddings.length === 0 ? (
        <div className="p-8 text-center text-[#A3A3A3]">No wedding inquiries yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <TableHeader>Bride</TableHeader>
              <TableHeader>Phone</TableHeader>
              <TableHeader>Package</TableHeader>
              <TableHeader>Location</TableHeader>
              <TableHeader>Wedding Date</TableHeader>
              <TableHeader>Party Size</TableHeader>
              <TableHeader>Bridal Code</TableHeader>
              <TableHeader>Date</TableHeader>
            </tr></thead>
            <tbody>
              {data.weddings.map((w) => (
                <tr key={w.id}>
                  <TableCell className="text-white">{w.bride_name}</TableCell>
                  <TableCell>{w.phone}</TableCell>
                  <TableCell><span className="text-xs text-[#C5A059]">{w.selected_package}</span></TableCell>
                  <TableCell>{(w.service_location || "").replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}</TableCell>
                  <TableCell>{w.wedding_date || "—"}</TableCell>
                  <TableCell>{w.party_size || "—"}</TableCell>
                  <TableCell className="text-[#C5A059] font-mono">{w.bridal_code}</TableCell>
                  <TableCell><span className="text-xs">{w.created_at?.split("T")[0]}</span></TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderBirthdays = () => {
    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];

    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Cake className="w-5 h-5 text-[#C5A059]" />
            <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">Birthday Promotions</h3>
          </div>
          <div className="flex items-center gap-3">
            <select value={birthdayMonth} onChange={(e) => setBirthdayMonth(e.target.value)}
              data-testid="birthday-month-select"
              className="bg-transparent border border-[#262626] text-white py-2 px-3 text-sm focus:border-[#C5A059] focus:outline-none" style={{ borderRadius: 0 }}>
              {months.map(m => <option key={m} value={m} className="bg-[#121212]">{m}</option>)}
            </select>
            <button onClick={handleSendBirthdayEmails} disabled={sendingEmails || birthdayClients.filter(c => c.email).length === 0}
              data-testid="send-birthday-emails-btn"
              className="flex items-center gap-2 bg-[#C5A059] text-[#050505] font-semibold text-xs tracking-[0.15em] uppercase px-4 py-2 hover:brightness-110 transition-all disabled:opacity-40">
              {sendingEmails ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send {birthdayMonth} Emails
            </button>
          </div>
        </div>

        {sendResult && (
          <div className="bento-card p-4 bg-emerald-500/5 border-emerald-500/20">
            <p className="text-emerald-400 text-sm">
              Sent {sendResult.sent} of {sendResult.total_recipients} birthday emails for {sendResult.month}.
              {sendResult.failed > 0 && <span className="text-red-400"> ({sendResult.failed} failed)</span>}
            </p>
          </div>
        )}

        <div className="bento-card p-6">
          <p className="font-body text-sm text-[#A3A3A3] mb-3">
            Each month, send a branded "Glo Into [Month]" email to all clients with birthdays that month. The email includes:
          </p>
          <ul className="space-y-1 text-xs text-[#A3A3A3] font-body">
            <li className="flex items-center gap-2"><CheckCircle size={12} className="text-[#C5A059]" /> 10% birthday discount on any service</li>
            <li className="flex items-center gap-2"><CheckCircle size={12} className="text-[#C5A059]" /> Weekly service specials (15% off rotating category)</li>
            <li className="flex items-center gap-2"><CheckCircle size={12} className="text-[#C5A059]" /> Exclusive package deals</li>
            <li className="flex items-center gap-2"><CheckCircle size={12} className="text-[#C5A059]" /> Beauty course discounts &amp; loyalty rewards</li>
          </ul>
        </div>

        <div className="bento-card overflow-hidden">
          <div className="p-6 border-b border-[#262626] flex justify-between items-center">
            <span className="text-white text-sm">{birthdayMonth} Birthday Clients: <strong>{birthdayClients.length}</strong></span>
            <span className="text-xs text-[#A3A3A3]">{birthdayClients.filter(c => c.email).length} with email</span>
          </div>
          {loadingBirthdays ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 text-[#C5A059] animate-spin mx-auto" /></div>
          ) : birthdayClients.length === 0 ? (
            <div className="p-8 text-center text-[#A3A3A3]">No clients with birthdays in {birthdayMonth}. Birthdays are captured when clients book services.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr>
                  <TableHeader>Name</TableHeader>
                  <TableHeader>Phone</TableHeader>
                  <TableHeader>Email</TableHeader>
                  <TableHeader>Total Visits</TableHeader>
                </tr></thead>
                <tbody>
                  {birthdayClients.map((c, i) => (
                    <tr key={i}>
                      <TableCell className="text-white">{c.name}</TableCell>
                      <TableCell>{c.phone}</TableCell>
                      <TableCell>{c.email || <span className="text-red-400 text-xs">No email</span>}</TableCell>
                      <TableCell>{c.total_punches}</TableCell>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const tabContent = {
    overview: renderOverview,
    bookings: renderBookings,
    birthdays: renderBirthdays,
    loyalty: renderLoyalty,
    weddings: renderWeddings,
    events: renderEvents,
    gift_cards: renderGiftCards,
    inquiries: renderInquiries,
    registrations: renderRegistrations,
    intake_forms: renderIntakeForms,
  };

  return (
    <div data-testid="admin-dashboard" className="pt-20 min-h-screen bg-[#050505]">
      <section className="py-8 bg-[#050505] border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">Admin</span>
              <h1 className="font-heading text-2xl text-white mt-1">Dashboard</h1>
            </div>
            <button onClick={fetchAllData} disabled={loading}
              className="flex items-center gap-2 border border-[#262626] text-[#A3A3A3] text-xs tracking-wider uppercase px-4 py-2 hover:border-[#C5A059] hover:text-white transition-all disabled:opacity-40">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />}
              Refresh
            </button>
          </div>
          <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-hide">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 text-xs tracking-wider uppercase whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? "bg-[#C5A059] text-[#050505] font-semibold"
                    : "text-[#A3A3A3] hover:text-white hover:bg-[#1a1a1a]"
                }`}
              >
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
            </div>
          ) : (
            tabContent[activeTab]?.()
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboardPage;
